(() => {
  const ENGRAVING_FONTS = ['Alice', 'Monotype Corsiva', 'Lobster Two', 'Astyca Script'];
  const EMBROIDERY_FONTS = ['Alice Bold', 'Bree Serif Bold', 'Arial Rounded', 'Salsa', 'Milonga', 'Monotype Corsiva'];

  const FONT_STYLES = {
    'Alice Bold': { family: '"Alice", serif', weight: '700', style: 'normal' },
    'Bree Serif Bold': { family: '"Bree Serif", serif', weight: '700', style: 'normal' },
    'Arial Rounded': {
      family: '"Arial Rounded MT Bold", "Arial Rounded MT", Arial, sans-serif',
      weight: '700',
      style: 'normal',
    },
    Salsa: { family: '"Salsa", cursive', weight: '400', style: 'normal' },
    Milonga: { family: '"Milonga", cursive', weight: '400', style: 'normal' },
    'Monotype Corsiva': {
      family: '"Monotype Corsiva", "Apple Chancery", "Segoe Script", "Brush Script MT", cursive',
      weight: '400',
      style: 'normal',
    },
  };

  const defaultStyle = { family: 'Arial, sans-serif', weight: '400', style: 'normal' };

  const replaceOptions = (select, fonts) => {
    const expected = ['', ...fonts];
    const current = [...select.options].map((option) => option.value);
    const alreadyCorrect = current.length === expected.length
      && current.every((value, index) => value === expected[index]);

    if (alreadyCorrect) return;

    const previous = select.value;
    select.replaceChildren(
      new Option('Choose a font', ''),
      ...fonts.map((font) => new Option(font, font)),
    );
    select.value = fonts.includes(previous) ? previous : '';
  };

  const setLabel = (label, text) => {
    const span = label?.querySelector(':scope > span');
    if (!span || span.dataset.dualFontLabel === text) return;

    span.replaceChildren(document.createTextNode(`${text} `));
    const required = document.createElement('em');
    required.setAttribute('aria-hidden', 'true');
    required.textContent = '*';
    span.append(required);
    span.dataset.dualFontLabel = text;
  };

  const setHelpText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };

  const createEmbroideryField = (root, engravingSelect, nameField) => {
    let select = root.querySelector('[data-bl-embroidery-font]');
    if (select) {
      replaceOptions(select, EMBROIDERY_FONTS);
      return select;
    }

    const label = document.createElement('label');
    label.className = 'bl-personalisation__field';

    const id = engravingSelect.id
      ? engravingSelect.id.replace('bl-personalisation-font-', 'bl-embroidery-font-')
      : `bl-embroidery-font-${root.dataset.productId || 'product'}`;
    label.htmlFor = id;

    const title = document.createElement('span');
    title.append(document.createTextNode('Embroidery font '));
    const required = document.createElement('em');
    required.setAttribute('aria-hidden', 'true');
    required.textContent = '*';
    title.append(required);

    select = document.createElement('select');
    select.id = id;
    select.name = 'properties[Embroidery font]';
    select.dataset.blEmbroideryFont = '';
    select.required = true;
    select.setAttribute('aria-required', 'true');

    const formId = engravingSelect.getAttribute('form');
    const describedBy = engravingSelect.getAttribute('aria-describedby');
    if (formId) select.setAttribute('form', formId);
    if (describedBy) select.setAttribute('aria-describedby', describedBy);

    replaceOptions(select, EMBROIDERY_FONTS);
    label.append(title, select);
    nameField.before(label);
    return select;
  };

  const configureRoot = (root) => {
    const engravingSelect = root.querySelector('[data-bl-font]');
    const nameInput = root.querySelector('[data-pb-comforter-name]');
    const nameField = nameInput?.closest('.bl-personalisation__field');
    const preview = root.querySelector('[data-pb-comforter-preview], [data-bl-comforter-preview]');
    const output = root.querySelector('[data-pb-comforter-preview-value]');

    if (!engravingSelect || !nameInput || !nameField || !preview || !output) return null;

    engravingSelect.name = 'properties[Engraving font]';
    replaceOptions(engravingSelect, ENGRAVING_FONTS);
    setLabel(engravingSelect.closest('.bl-personalisation__field'), 'Engraving font');

    setHelpText(
      root.querySelector('.bl-personalisation__section-heading--plaque span'),
      'Choose the engraving font below. It controls the suitcase plaque only.',
    );
    setHelpText(
      root.querySelector('.bl-personalisation__section-heading--comforter span'),
      'Choose the embroidery font below. The white box previews the comforter name only.',
    );

    if (preview.hasAttribute('data-pb-comforter-preview')) {
      preview.removeAttribute('data-pb-comforter-preview');
      preview.setAttribute('data-bl-comforter-preview', '');
    }

    const embroiderySelect = createEmbroideryField(root, engravingSelect, nameField);
    return { engravingSelect, embroiderySelect, nameInput, preview, output };
  };

  const fitPreviewText = (box, output) => {
    let size = window.matchMedia('(max-width: 480px)').matches ? 42 : 48;
    output.style.fontSize = `${size}px`;

    window.requestAnimationFrame(() => {
      const availableWidth = Math.max(box.clientWidth - 32, 1);
      const availableHeight = Math.max(box.clientHeight - 24, 1);
      let guard = 0;

      while (
        (output.scrollWidth > availableWidth || output.scrollHeight > availableHeight)
        && size > 18
        && guard < 80
      ) {
        size -= 1;
        output.style.fontSize = `${size}px`;
        guard += 1;
      }
    });
  };

  const updatePreview = (fields) => {
    const enteredName = fields.nameInput.value.trimEnd();
    const style = FONT_STYLES[fields.embroiderySelect.value] || defaultStyle;

    fields.output.textContent = enteredName || 'Your name';
    fields.output.style.fontFamily = style.family;
    fields.output.style.fontWeight = style.weight;
    fields.output.style.fontStyle = style.style;
    fields.output.style.color = '#111111';
    fields.preview.dataset.empty = enteredName ? 'false' : 'true';
    fitPreviewText(fields.preview, fields.output);
  };

  const bindRoot = (root, attempt = 0) => {
    const fields = configureRoot(root);
    if (!fields) return;

    if (root.dataset.blDualFontsBound === 'true') {
      updatePreview(fields);
      return;
    }

    if (typeof root.validate !== 'function' || typeof root.applyMode !== 'function') {
      if (attempt < 20) window.setTimeout(() => bindRoot(root, attempt + 1), 50);
      return;
    }

    root.dataset.blDualFontsBound = 'true';

    const originalValidate = root.validate.bind(root);
    const originalApplyMode = root.applyMode.bind(root);

    root.applyMode = function applyModeWithDualFonts() {
      originalApplyMode();
      const personalised = this.isPersonalised();

      fields.embroiderySelect.disabled = !personalised;
      fields.embroiderySelect.required = personalised;
      fields.nameInput.disabled = !personalised;
      fields.nameInput.required = personalised;

      if (!personalised) {
        fields.embroiderySelect.setCustomValidity('');
        fields.embroiderySelect.removeAttribute('aria-invalid');
        fields.nameInput.setCustomValidity('');
        fields.nameInput.removeAttribute('aria-invalid');
      }
    };

    root.validate = function validateWithDualFonts(markErrors = false) {
      fields.embroiderySelect.setCustomValidity('');
      fields.embroiderySelect.removeAttribute('aria-invalid');
      fields.nameInput.setCustomValidity('');
      fields.nameInput.removeAttribute('aria-invalid');

      const baseResult = originalValidate(markErrors);
      if (!baseResult.valid || !this.isPersonalised()) return baseResult;

      if (!EMBROIDERY_FONTS.includes(fields.embroiderySelect.value)) {
        const result = {
          valid: false,
          message: 'Choose an embroidery font.',
          field: fields.embroiderySelect,
        };
        if (markErrors) this.markFieldInvalid(result.field, result.message);
        return result;
      }

      if (fields.nameInput.value.trim() === '') {
        const result = {
          valid: false,
          message: 'Enter the comforter name.',
          field: fields.nameInput,
        };
        if (markErrors) this.markFieldInvalid(result.field, result.message);
        return result;
      }

      return baseResult;
    };

    const refresh = () => updatePreview(fields);
    root.addEventListener('input', refresh);
    root.addEventListener('change', refresh);
    window.addEventListener('resize', refresh);

    root.applyMode();
    updatePreview(fields);
    root.refreshValidation(false);
    document.fonts?.ready?.then(refresh);
  };

  const bindAll = (scope = document) => {
    if (scope instanceof Element && scope.matches('bl-personalisation')) bindRoot(scope);
    scope.querySelectorAll?.('bl-personalisation').forEach((root) => bindRoot(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindAll(), { once: true });
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
})();
